import type { PropsWithChildren } from "react";
import { createContext, useMemo, useState } from "react";
import type { ObjectCardCheck } from "../../api/types/objectTypes";
import type { DocumentCitation } from "./helpers";
import type { ObjectCardPreviewConfig } from "./objectCardPreviewState";
import type { ObjectCardSource } from "./objectCardSource";

export type ObjectCardPreviewChecks = ObjectCardCheck[] | "unavailable";

interface ContextValue {
  documentCitations: DocumentCitation[];
  activeCitationKey?: string;
  selectCitation: (key: string) => void;
  setRelatedCitations: (citations: DocumentCitation[]) => void;
  cardPreview?: ObjectCardPreviewConfig;
  source?: ObjectCardSource;
  setPreviewChecks?: (checks: ObjectCardPreviewChecks) => void;
  setHasUnsavedEdits?: (hasUnsavedEdits: boolean) => void;
}

const ObjectCardContext = createContext<ContextValue | undefined>(undefined);

interface Props {
  fieldCitations?: DocumentCitation[];
  cardPreview?: ObjectCardPreviewConfig;
  source?: ObjectCardSource;
  onPreviewChecksChange?: (checks: ObjectCardPreviewChecks) => void;
  onUnsavedEditsChange?: (hasUnsavedEdits: boolean) => void;
}

export const ObjectCardContextProvider = ({
  fieldCitations,
  cardPreview,
  source,
  onPreviewChecksChange,
  onUnsavedEditsChange,
  children,
}: PropsWithChildren<Props>) => {
  const [activeCitationKey, setActiveCitationKey] = useState<string>();
  const [relatedCitations, setRelatedCitations] = useState<DocumentCitation[]>([]);

  const documentCitations = useMemo<DocumentCitation[]>(
    () => [...(fieldCitations ?? []), ...relatedCitations],
    [fieldCitations, relatedCitations]
  );

  const value = useMemo<ContextValue>(
    () => ({
      documentCitations,
      activeCitationKey,
      selectCitation: setActiveCitationKey,
      setRelatedCitations,
      cardPreview,
      source,
      setPreviewChecks: onPreviewChecksChange,
      setHasUnsavedEdits: onUnsavedEditsChange,
    }),
    [documentCitations, activeCitationKey, cardPreview, source, onPreviewChecksChange, onUnsavedEditsChange]
  );

  return <ObjectCardContext value={value}>{children}</ObjectCardContext>;
};

export default ObjectCardContext;
